import { defaultLocale, isSupportedLocale, supportedLocales, type Locale } from './config';

const localePrefixPattern = new RegExp(`^/(${supportedLocales.join('|')})(?=/|$)`, 'i');

function normalizePathname(pathname: string): string {
  const normalizedPathname = pathname.trim() || '/';
  return normalizedPathname.startsWith('/') ? normalizedPathname : `/${normalizedPathname}`;
}

function parseWeightedLanguageTag(value: string): { tag: string; q: number } {
  const [rawTag, ...parameterParts] = value.split(';').map((segment) => segment.trim());
  if (!rawTag) {
    return { tag: '', q: 0 };
  }

  const qParameter = parameterParts.find((parameter) => parameter.toLowerCase().startsWith('q='));
  const qValue = qParameter ? Number.parseFloat(qParameter.slice(2)) : 1;

  return {
    tag: rawTag.toLowerCase(),
    q: Number.isFinite(qValue) ? qValue : 1,
  };
}

export function resolveLocaleFromAcceptLanguage(
  acceptLanguageHeader: string | null | undefined
): Locale {
  if (!acceptLanguageHeader?.trim()) {
    return defaultLocale;
  }

  const weightedTags = acceptLanguageHeader
    .split(',')
    .map(parseWeightedLanguageTag)
    .filter((entry) => entry.tag.length > 0)
    .sort((left, right) => right.q - left.q);

  for (const weightedTag of weightedTags) {
    const [languageCode] = weightedTag.tag.split('-');
    if (isSupportedLocale(weightedTag.tag)) {
      return weightedTag.tag;
    }

    if (isSupportedLocale(languageCode)) {
      return languageCode;
    }

    if (weightedTag.tag.startsWith('ua')) {
      return 'uk';
    }
  }

  return defaultLocale;
}

export function getLocaleFromPathname(pathname: string | null | undefined): Locale | null {
  if (!pathname?.trim()) {
    return null;
  }

  const normalizedPathname = normalizePathname(pathname);
  const match = normalizedPathname.match(localePrefixPattern);
  if (!match) {
    return null;
  }

  const localeCandidate = match[1]?.toLowerCase();
  return isSupportedLocale(localeCandidate) ? localeCandidate : null;
}

export function stripLocalePrefix(pathname: string): {
  locale: Locale | null;
  pathname: string;
} {
  const normalizedPathname = normalizePathname(pathname);
  const locale = getLocaleFromPathname(normalizedPathname);

  if (!locale) {
    return {
      locale: null,
      pathname: normalizedPathname,
    };
  }

  const trimmedPathname = normalizedPathname.replace(localePrefixPattern, '') || '/';
  return {
    locale,
    pathname: trimmedPathname.startsWith('/') ? trimmedPathname : `/${trimmedPathname}`,
  };
}

function isExternalHref(href: string): boolean {
  return /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(href);
}

export function withLocale(pathname: string, locale: Locale): string {
  const normalizedPathname = pathname.trim() || '/';
  if (isExternalHref(normalizedPathname) || normalizedPathname.startsWith('#')) {
    return normalizedPathname;
  }

  const withLeadingSlash = normalizePathname(normalizedPathname);

  const hashIndex = withLeadingSlash.indexOf('#');
  const hashFragment = hashIndex >= 0 ? withLeadingSlash.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? withLeadingSlash.slice(0, hashIndex) : withLeadingSlash;

  const queryIndex = withoutHash.indexOf('?');
  const queryString = queryIndex >= 0 ? withoutHash.slice(queryIndex) : '';
  const pathWithoutQuery = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;

  const strippedPath = stripLocalePrefix(pathWithoutQuery).pathname;
  const localizedPath = strippedPath === '/' ? `/${locale}` : `/${locale}${strippedPath}`;

  return `${localizedPath}${queryString}${hashFragment}`;
}

export function isBypassPath(pathname: string): boolean {
  if (
    pathname.startsWith('/_next') ||
    pathname.startsWith('/api') ||
    pathname.startsWith('/graphql')
  ) {
    return true;
  }

  if (
    pathname === '/robots.txt' ||
    pathname === '/sitemap.xml' ||
    pathname === '/sitemap-index.xml' ||
    pathname === '/llms.txt' ||
    pathname === '/rss.xml' ||
    pathname === '/openapi.json'
  ) {
    return true;
  }

  return /\.[a-zA-Z0-9]+$/.test(pathname);
}
